import {Maybe, Member, ProjectMember, Role} from '../../../generated/graphql';
import {Viewer} from '../../../state/viewer/types';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {TeamMemberRole} from '../../MembersTable/TeamMembersTable/utils';
import {RoleOption} from '../../MembersTable/TeamOrOrganizationRoleOption';

// similar to app/src/components/Registries/RegistrySettings/utils.ts
export type ProjectRoleMember = Pick<
  ProjectMember,
  | 'id'
  | 'username'
  | 'name'
  | 'photoUrl'
  | 'email'
  | 'accountType'
  | 'projectRoleIsInherited'
> & {role: Pick<Role, 'ID' | 'name'>};

export type TeamMemberData = Pick<Member, 'id' | 'username'> & {
  memberRole?: Maybe<Pick<Role, 'ID' | 'name'>> | undefined;
};

export type TeamProjectMember = {
  username: string;
  name: string;
  email: string;
  id: string;
  photoUrl: string;
  accountType: string;
  role: string;
  teamRole: string;
  projectRoleIsInherited: boolean;
};

export const getViewerTeamRole = (
  viewer: Viewer | undefined,
  members: TeamMemberData[]
): string => {
  const teamMember = members.find(
    member => member?.id && member.id === viewer?.id
  );
  return teamMember?.memberRole?.name ?? '';
};

/**
 * Utility to convert project members to team project members
 * @param teamMembers
 * @param projectMembers
 */
export const getTeamProjectMembers = (
  teamMembers: TeamMemberData[],
  projectMembers: ProjectRoleMember[]
): TeamProjectMember[] => {
  const teamMembersMap = new Map<string, TeamMemberData>();
  const projectMembersMap = new Map<string, ProjectRoleMember>();
  teamMembers.forEach(member => {
    const memberId = member.id ?? '';
    if (memberId.length > 0) {
      teamMembersMap.set(memberId, member);
    }
  });
  projectMembers.forEach(member => {
    projectMembersMap.set(member.id, member);
  });

  const teamProjectMembers: TeamProjectMember[] = [];
  projectMembersMap.forEach((member, memberId) => {
    const teamMember = teamMembersMap.get(memberId);
    const teamProjectMember: TeamProjectMember = {
      username: member.username ?? '',
      name: member.name ?? '',
      email: member.email ?? '',
      id: memberId,
      photoUrl: member.photoUrl ?? '',
      accountType: member.accountType,
      role: member.role.name,
      teamRole: teamMember?.memberRole?.name ?? '',
      projectRoleIsInherited: member.projectRoleIsInherited,
    };
    teamProjectMembers.push(teamProjectMember);
  });
  return teamProjectMembers;
};

export const getRoleLabel = (member: TeamProjectMember): string => {
  if (checkIsServiceAccount(member.accountType)) {
    return 'Service';
  }
  return getRoleText(member.role);
};

export const getRoleText = (role: string): string => {
  switch (role) {
    case TeamMemberRole.ADMIN:
      return 'Admin';
    case TeamMemberRole.MEMBER:
      return 'Member';
    case TeamMemberRole.VIEW_ONLY:
      return 'View-Only';
    default:
      // custom role case
      return role;
  }
};

export type ProjectRoleOption = {
  value: string;
  role: string;
  isDisabled: boolean;
};

export function getProjectRoleOptionFromRole(role: string): ProjectRoleOption {
  return {
    role: role,
    value: getRoleText(role),
    isDisabled: false,
  };
}

export function getProjectRoleOptionFromRoleOption(
  role: RoleOption
): ProjectRoleOption {
  return {
    role: role.value,
    value: getRoleText(role.value),
    isDisabled: role.isDisabled === true,
  };
}
